/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ActualSizeSticker from './ActualSizeSticker';
import MaterialIcon from './MaterialIcon';
import { ASSET_PATHS } from '../config/paths';
import parallaxService from '../services/parallaxService';
import backgroundService from '../services/backgroundService';

import logger from '../utils/logger.js';
import './ParallaxComponent.css';

// Simple throttle function for scroll events
const throttle = (func, limit) => {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Default speed matrix (simple, static)
const DEFAULT_SPEEDS = {
  LARGE: 21.0,
  MEDIUM: 7.8,
  SMALL: 3.0,
  MICRO: 2.0,
  NANO: 1.5,
  PIXEL: 1.0,
  ATOM: 0.5,
};

// Default Y positions (simple mapping)
const DEFAULT_Y_POSITIONS = {
  LARGE: 5, // Closest
  MEDIUM: 20,
  SMALL: 35,
  MICRO: 50,
  NANO: 65,
  PIXEL: 80,
  ATOM: 95, // Furthest
};

const ParallaxComponent = ({
  // Basic props
  category = 'dinosaurs',
  availableSizes = ['ATOM', 'PIXEL', 'NANO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE'],
  height = '50vh',
  instanceId = 'clean-parallax',

  // Sticker data
  sceneStickers = [],

  // Scene loading
  loadSceneName = null, // Optional: load specific scene by name

  // Callbacks
  onStickerClick = null,
  onSceneStickersUpdate = null,
  onSceneNameUpdate = null,

  // Admin panel
  showAdmin = false,

  // Timing control
  isInitialAppLoad = true, // Skip hero animation wait if not initial load
}) => {
  // Refs
  const parallaxRef = useRef(null);

  // Viewport detection state
  const [isInViewport, setIsInViewport] = useState(false);
  const [viewportEntryScroll, setViewportEntryScroll] = useState(0);

  // Basic state
  const [scrollOffset, setScrollOffset] = useState(0);
  const [adminOpen, setAdminOpen] = useState(false);

  // Layer state
  const [layerYPositions, setLayerYPositions] = useState(() => {
    const positions = {};
    availableSizes.forEach((size) => {
      positions[size] = DEFAULT_Y_POSITIONS[size] || 50;
    });
    return positions;
  });

  const [layerVisibility, setLayerVisibility] = useState(() => {
    const visibility = {};
    availableSizes.forEach((size) => {
      visibility[size] = true; // Always show all layers
    });
    return visibility;
  });

  const [layerSpeeds, setLayerSpeeds] = useState({});

  // Component settings
  const [componentHeight, setComponentHeight] = useState(height);
  const [parallaxMode, setParallaxMode] = useState('normal');

  // Database state
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [sceneName, setSceneName] = useState(`${instanceId}_scene`);
  const [savedScenes, setSavedScenes] = useState([]);
  const [currentSceneName, setCurrentSceneName] = useState(null); // Track which scene this instance is currently showing
  const [isLoadingScene, setIsLoadingScene] = useState(false); // Instance-specific loading state

  // Category scene assignment state
  const [categories, setCategories] = useState([]);
  const [categoryAssignments, setCategoryAssignments] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [assigningToCategory, setAssigningToCategory] = useState(false);

  // Listing-based scene insertion state
  const [listingSearchTerm, setListingSearchTerm] = useState('');
  const [listingSearchResults, setListingSearchResults] = useState([]);
  const [selectedListingForInsertion, setSelectedListingForInsertion] = useState(null);
  const [selectedSceneForInsertion, setSelectedSceneForInsertion] = useState('');
  const [assigningSceneToListing, setAssigningSceneToListing] = useState(false);
  const [listingSceneInsertions, setListingSceneInsertions] = useState([]);

  // Loading animation state
  const [isInitialized, setIsInitialized] = useState(false);
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
  const [instanceBackgroundPath, setInstanceBackgroundPath] = useState(null);
  const [backgroundStyle, setBackgroundStyle] = useState({
    size: 'cover',
    position: 'center',
    repeat: 'no-repeat',
    attachment: 'scroll',
  });
  const [backgroundAccordionOpen, setBackgroundAccordionOpen] = useState(false);
  const [layerControlsAccordionOpen, setLayerControlsAccordionOpen] = useState(false);
  const [targetHeight, setTargetHeight] = useState(height); // Target height for animation
  const [isSceneLoaded, setIsSceneLoaded] = useState(false); // Track if scene data is loaded
  const [areImagesLoaded, setAreImagesLoaded] = useState(false); // Track if svgPath images are loaded
  const [loadingProgress, setLoadingProgress] = useState(0); // Loading progress (0-100)
  const [imageLoadingProgress, setImageLoadingProgress] = useState(0); // Image loading progress (0-100)

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);

  // Sticker data state - separate from positions
  const [loadedStickerData, setLoadedStickerData] = useState(() => {
    return sceneStickers;
  });

  // Debug: Log when loadedStickerData changes
  useEffect(() => {
    if (loadedStickerData.length > 0) {
      logger.log(
        `✅ [${instanceId}] loadedStickerData updated:`,
        loadedStickerData.length,
        'stickers'
      );
    }
  }, [loadedStickerData, instanceId]);

  // Sticker positions state - initialize with existing positions
  const [stickerPositions, setStickerPositions] = useState(() => {
    const positions = {};
    sceneStickers.forEach((sticker, index) => {
      const stickerId = `${sticker.listingId}-${sticker.size}-${index}`;
      positions[stickerId] = {
        x: sticker.xOffset || 0,
        y: sticker.yOffset || 0,
      };
    });
    return positions;
  });

  // Scroll detection
  useEffect(() => {
    const handleScroll = throttle(() => {
      setScrollOffset(window.scrollY);
    }, 16); // ~60fps

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Viewport detection with Intersection Observer
  useEffect(() => {
    if (!parallaxRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasInViewport = isInViewport;
          setIsInViewport(entry.isIntersecting);

          if (entry.isIntersecting && !wasInViewport) {
            const entryScroll = window.scrollY;
            setViewportEntryScroll(entryScroll);
            // Component entered viewport
          } else if (!entry.isIntersecting && wasInViewport) {
            // Component left viewport
          }
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: '0px', // Start detecting exactly at viewport edge
        threshold: 0, // Trigger as soon as any part is visible
      }
    );

    observer.observe(parallaxRef.current);

    return () => {
      if (parallaxRef.current) {
        observer.unobserve(parallaxRef.current);
      }
    };
  }, [instanceId, isInViewport]);

  // Load categories and assignments for admin panel
  useEffect(() => {
    if (showAdmin) {
      const loadCategoriesAndAssignments = async () => {
        try {
          const [categoriesData, assignmentsData] = await Promise.all([
            parallaxService.getAllCategories(),
            parallaxService.getAllCategorySceneAssignments(),
          ]);

          setCategories(categoriesData);

          const assignmentsMap = {};
          assignmentsData.forEach((assignment) => {
            assignmentsMap[assignment.category] = assignment.scene_name;
          });
          setCategoryAssignments(assignmentsMap);
        } catch (error) {
          console.error('❌ Error loading categories and assignments:', error);
        }
      };

      loadCategoriesAndAssignments();
    }
  }, [showAdmin]);

  // Handle listing search with debouncing
  useEffect(() => {
    if (!listingSearchTerm || listingSearchTerm.length < 2) {
      setListingSearchResults([]);
      return;
    }

    const delayedSearch = setTimeout(async () => {
      try {
        // Use the existing database service to search listings
        const response = await fetch(
          `/.netlify/functions/local-db/listings-paginated?page=1&limit=50&search=${encodeURIComponent(listingSearchTerm)}`
        );
        if (response.ok) {
          const data = await response.json();
          setListingSearchResults(data.data || []);
        } else {
          console.error('❌ Error searching listings:', response.status);
          setListingSearchResults([]);
        }
      } catch (error) {
        console.error('❌ Error searching listings:', error);
        setListingSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayedSearch);
  }, [listingSearchTerm]);

  // Function to refresh scenes list
  const refreshScenesList = useCallback(async () => {
    try {
      const scenes = await parallaxService.getAllScenes();
      const scenesArray = Array.isArray(scenes) ? scenes : scenes?.data || [];
      setSavedScenes(scenesArray);
    } catch (error) {
      console.error(`❌ [${instanceId}] Error refreshing scenes list:`, error);
    }
  }, [instanceId]);

  // Load saved scenes and auto-load the appropriate scene on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let isMounted = true;
    let hasLoaded = false; // Track if we've already loaded to prevent duplicates

    const loadSavedScenes = async () => {
      if (hasLoaded) return; // Prevent duplicate calls
      hasLoaded = true;

      setLoadingProgress(10); // Started loading

      try {
        const scenes = await parallaxService.getAllScenes();
        setLoadingProgress(30); // Scenes loaded
        // Handle both array and object with data property
        const scenesArray = Array.isArray(scenes) ? scenes : scenes?.data || [];

        if (!isMounted) return;
        setSavedScenes(scenesArray);

        // Check if this instance has a previously assigned scene in the database
        setLoadingProgress(50); // Checking assignment

        let assignment = null;
        let sceneNameToLoad = null;

        // Check if this is a category instance (starts with "category-")
        if (instanceId.startsWith('category-')) {
          // Extract category name from instanceId (e.g., "category-dinosaur-stickers" -> "Dinosaur Stickers")
          const categoryName = instanceId
            .replace('category-', '')
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          // Get category scene assignment
          const categoryAssignment = await parallaxService.getCategorySceneAssignment(categoryName);

          if (categoryAssignment && categoryAssignment.scene_name) {
            assignment = { data: { scene_name: categoryAssignment.scene_name } };
            sceneNameToLoad = categoryAssignment.scene_name;
          } else if (loadSceneName) {
            // Fallback: explicit scene name provided by parent
            sceneNameToLoad = loadSceneName;
          }
        } else if (loadSceneName) {
          // Load specific scene by name (for homepage)
          sceneNameToLoad = loadSceneName;
        } else {
          // Regular instance assignment
          assignment = await parallaxService.getInstanceAssignment(instanceId);
          if (assignment && assignment.data) {
            sceneNameToLoad = assignment.data.scene_name;
            // Load instance-specific background path
            if (assignment.data.background_path) {
              setInstanceBackgroundPath(assignment.data.background_path);
            }
          }
        }

        if (sceneNameToLoad && scenesArray.some((scene) => scene.name === sceneNameToLoad)) {
          // Found assigned scene
          // Load the previously assigned scene for this instance
          logger.log(`📂 [${instanceId}] Loading assigned scene: "${sceneNameToLoad}"`);
          setLoadingProgress(70); // Loading assigned scene
          if (isMounted && !isLoadingScene) {
            setIsLoadingScene(true);
            setIsLoading(true);
            try {
              const scene = await parallaxService.loadScene(sceneNameToLoad);
              logger.log(`📦 [${instanceId}] Scene loaded:`, scene);
              logger.log(`🎯 [${instanceId}] Scene keys:`, scene ? Object.keys(scene) : 'null');
              if (scene && scene.scene_stickers) {
                logger.log(`🎯 [${instanceId}] Scene stickers field:`, scene.scene_stickers);
                logger.log(`🎯 [${instanceId}] Scene stickers type:`, typeof scene.scene_stickers);
                logger.log(
                  `🎯 [${instanceId}] Scene stickers length:`,
                  Array.isArray(scene.scene_stickers) ? scene.scene_stickers.length : 'NOT ARRAY'
                );
              }

              if (scene && isMounted) {
                const sceneData = scene;

                // Load the data
                if (sceneData.layer_y_positions) {
                  try {
                    // API already parses JSON - use data directly
                    const yPositions = sceneData.layer_y_positions;
                    const mappedYPositions = {};
                    if (yPositions['ATOM'] !== undefined) {
                      availableSizes.forEach((size) => {
                        mappedYPositions[size] = yPositions[size] || 0;
                      });
                    } else {
                      availableSizes.forEach((size, index) => {
                        const layerKey = `layer-${index + 1}`;
                        mappedYPositions[size] = yPositions[layerKey] || 0;
                      });
                    }
                    setLayerYPositions(mappedYPositions);
                  } catch (e) {
                    console.error('Error parsing Y positions:', e);
                  }
                }
                if (sceneData.layer_visibility) {
                  try {
                    // API already parses JSON - use data directly
                    const visibility = sceneData.layer_visibility;
                    const mappedVisibility = {};
                    if (visibility['ATOM'] !== undefined) {
                      availableSizes.forEach((size) => {
                        mappedVisibility[size] = visibility[size] !== false;
                      });
                    } else {
                      availableSizes.forEach((size, index) => {
                        const layerKey = `layer-${index + 1}`;
                        mappedVisibility[size] = visibility[layerKey] !== false;
                      });
                    }
                    setLayerVisibility(mappedVisibility);
                  } catch (e) {
                    console.error('Error parsing visibility:', e);
                  }
                }
                if (sceneData.layer_speeds) {
                  try {
                    // API already parses JSON - use data directly
                    const speeds = sceneData.layer_speeds;
                    const mappedSpeeds = {};
                    if (speeds['ATOM'] !== undefined) {
                      availableSizes.forEach((size) => {
                        mappedSpeeds[size] = speeds[size] || 0;
                      });
                    } else {
                      availableSizes.forEach((size, index) => {
                        const layerKey = `layer-${index + 1}`;
                        mappedSpeeds[size] = speeds[layerKey] || 0;
                      });
                    }
                    setLayerSpeeds(mappedSpeeds);
                  } catch (e) {
                    console.error('Error parsing speeds:', e);
                  }
                }
                if (sceneData.component_height) {
                  setComponentHeight(sceneData.component_height);
                  setTargetHeight(sceneData.component_height);
                }

                // Load sticker data
                let sceneStickerData = [];
                if (sceneData.scene_stickers) {
                  try {
                    // API already parses JSON - use data directly
                    const savedStickerData = sceneData.scene_stickers;
                    logger.log(
                      '🔍 Loading scene stickers - Type:',
                      typeof savedStickerData,
                      'Length:',
                      Array.isArray(savedStickerData) ? savedStickerData.length : 'NOT ARRAY',
                      'Data:',
                      savedStickerData
                    );

                    // Validate and clean the loaded sticker data
                    const cleanedStickerData = savedStickerData.map((sticker, index) => {
                      const validSize = [
                        'ATOM',
                        'PIXEL',
                        'NANO',
                        'MICRO',
                        'SMALL',
                        'MEDIUM',
                        'LARGE',
                        'JUMBO',
                      ].includes(sticker.size)
                        ? sticker.size
                        : 'MICRO';

                      const cleanedSticker = {
                        listingId: sticker.listingId?.toString() || `fallback-${index}`,
                        title: sticker.name || sticker.title || 'Untitled Sticker', // Fix: Check 'name' field first
                        size: validSize,
                        color: sticker.color || 'Black (Matte)',
                        category: sticker.category || 'stickers',
                        svgPath: sticker.svgPath || null,
                        xOffset: sticker.xOffset || 0,
                        yOffset: sticker.yOffset || 0,
                      };

                      return cleanedSticker;
                    });

                    setLoadedStickerData(cleanedStickerData);
                    sceneStickerData = cleanedStickerData;
                    logger.log('✅ Set loadedStickerData:', cleanedStickerData.length, 'stickers');
                    logger.log('🔍 First 3 stickers:', cleanedStickerData.slice(0, 3));
                  } catch (e) {
                    console.error('Error parsing sticker data:', e);
                  }
                }

                // Load sticker positions
                if (sceneData.sticker_positions) {
                  try {
                    // API already parses JSON - use data directly
                    const savedPositions = sceneData.sticker_positions;

                    setStickerPositions(savedPositions);
                  } catch (e) {
                    console.error('Error parsing sticker positions:', e);
                  }
                }

                // Load background path from scene data
                if (sceneData.background_path) {
                  setInstanceBackgroundPath(sceneData.background_path);
                }

                setSceneName(sceneNameToLoad);
                setCurrentSceneName(sceneNameToLoad);

                if (onSceneNameUpdate) {
                  onSceneNameUpdate(sceneNameToLoad);
                }

                // Don't automatically save instance assignment - let user choose

                // Mark scene as loaded and start image preloading
                setLoadingProgress(90); // Scene data processed
                setIsSceneLoaded(true);

                // Preload scene images
                if (sceneStickerData && sceneStickerData.length > 0) {
                  preloadSceneImages(sceneStickerData);
                } else {
                  setAreImagesLoaded(true);
                  setImageLoadingProgress(100);
                }
              }
            } catch (error) {
              console.error('❌ Error loading scene:', error);
            } finally {
              if (isMounted) {
                setIsLoading(false);
                setIsLoadingScene(false);
              }
            }
          }
        } else if (scenesArray.length > 0) {
          // No previously assigned scene, load the first scene as default
          const firstScene = scenesArray[0];
          if (isMounted && !isLoadingScene) {
            setIsLoadingScene(true);
            setIsLoading(true);
            try {
              const scene = await parallaxService.loadScene(firstScene.name);

              if (scene && isMounted) {
                const sceneData = scene;

                // Load the data (same logic as above)
                if (sceneData.layer_y_positions) {
                  try {
                    // API already parses JSON - use data directly
                    const yPositions = sceneData.layer_y_positions;
                    const mappedYPositions = {};
                    if (yPositions['ATOM'] !== undefined) {
                      availableSizes.forEach((size) => {
                        mappedYPositions[size] = yPositions[size] || 0;
                      });
                    } else {
                      availableSizes.forEach((size, index) => {
                        const layerKey = `layer-${index + 1}`;
                        mappedYPositions[size] = yPositions[layerKey] || 0;
                      });
                    }
                    setLayerYPositions(mappedYPositions);
                  } catch (e) {
                    console.error('Error parsing Y positions:', e);
                  }
                }
                if (sceneData.layer_visibility) {
                  try {
                    // API already parses JSON - use data directly
                    const visibility = sceneData.layer_visibility;
                    const mappedVisibility = {};
                    if (visibility['ATOM'] !== undefined) {
                      availableSizes.forEach((size) => {
                        mappedVisibility[size] = visibility[size] !== false;
                      });
                    } else {
                      availableSizes.forEach((size, index) => {
                        const layerKey = `layer-${index + 1}`;
                        mappedVisibility[size] = visibility[layerKey] !== false;
                      });
                    }
                    setLayerVisibility(mappedVisibility);
                  } catch (e) {
                    console.error('Error parsing visibility:', e);
                  }
                }
                if (sceneData.layer_speeds) {
                  try {
                    // API already parses JSON - use data directly
                    const speeds = sceneData.layer_speeds;
                    const mappedSpeeds = {};
                    if (speeds['ATOM'] !== undefined) {
                      availableSizes.forEach((size) => {
                        mappedSpeeds[size] = speeds[size] || 0;
                      });
                    } else {
                      availableSizes.forEach((size, index) => {
                        const layerKey = `layer-${index + 1}`;
                        mappedSpeeds[size] = speeds[layerKey] || 0;
                      });
                    }
                    setLayerSpeeds(mappedSpeeds);
                  } catch (e) {
                    console.error('Error parsing speeds:', e);
                  }
                }
                if (sceneData.component_height) {
                  setComponentHeight(sceneData.component_height);
                  setTargetHeight(sceneData.component_height);
                }

                // Load sticker data
                let firstSceneStickerData = [];
                if (sceneData.scene_stickers) {
                  try {
                    // API already parses JSON - use data directly
                    const savedStickerData = sceneData.scene_stickers;

                    // Validate and clean the loaded sticker data
                    const cleanedStickerData = savedStickerData.map((sticker, index) => {
                      const validSize = [
                        'ATOM',
                        'PIXEL',
                        'NANO',
                        'MICRO',
                        'SMALL',
                        'MEDIUM',
                        'LARGE',
                        'JUMBO',
                      ].includes(sticker.size)
                        ? sticker.size
                        : 'MICRO';

                      const cleanedSticker = {
                        listingId: sticker.listingId?.toString() || `fallback-${index}`,
                        title: sticker.name || sticker.title || 'Untitled Sticker', // Fix: Check 'name' field first
                        size: validSize,
                        color: sticker.color || 'Black (Matte)',
                        category: sticker.category || 'stickers',
                        svgPath: sticker.svgPath || null,
                        xOffset: sticker.xOffset || 0,
                        yOffset: sticker.yOffset || 0,
                      };

                      return cleanedSticker;
                    });

                    setLoadedStickerData(cleanedStickerData);
                    firstSceneStickerData = cleanedStickerData;
                  } catch (e) {
                    console.error('Error parsing sticker data:', e);
                  }
                }

                // Load sticker positions
                if (sceneData.sticker_positions) {
                  try {
                    // API already parses JSON - use data directly
                    const savedPositions = sceneData.sticker_positions;

                    setStickerPositions(savedPositions);
                  } catch (e) {
                    console.error('Error parsing sticker positions:', e);
                  }
                }

                setSceneName(firstScene.name);
                setCurrentSceneName(firstScene.name);

                if (onSceneNameUpdate) {
                  onSceneNameUpdate(firstScene.name);
                }

                // Don't automatically save instance assignment - let user choose

                // Mark scene as loaded and start image preloading
                setLoadingProgress(90); // Scene data processed
                setIsSceneLoaded(true);

                // Preload scene images
                if (firstSceneStickerData && firstSceneStickerData.length > 0) {
                  preloadSceneImages(firstSceneStickerData);
                } else {
                  setAreImagesLoaded(true);
                  setImageLoadingProgress(100);
                }
              }
            } catch (error) {
              console.error('❌ Error loading scene:', error);
            } finally {
              if (isMounted) {
                setIsLoading(false);
                setIsLoadingScene(false);
              }
            }
          }
        }

        // If no scene was loaded, mark as loaded with default height
        if (isMounted && !isSceneLoaded) {
          setLoadingProgress(100); // No scene to load
          setIsSceneLoaded(true);
          setAreImagesLoaded(true); // No images to load
          setImageLoadingProgress(100);
        }
      } catch (error) {
        console.error('❌ Error loading saved scenes:', error);
        if (isMounted) {
          setSavedScenes([]);
          // Mark as loaded even if there's an error
          setLoadingProgress(100); // Error fallback
          setIsSceneLoaded(true);
          setAreImagesLoaded(true); // No images to load
          setImageLoadingProgress(100);
        }
      }
    };

    loadSavedScenes();

    return () => {
      isMounted = false;
    };
  }, [instanceId]); // Only depend on instanceId to prevent infinite loops

  // Register for scene update notifications (database changes)
  useEffect(() => {
    parallaxService.addSceneUpdateListener(refreshScenesList);

    return () => {
      parallaxService.removeSceneUpdateListener(refreshScenesList);
    };
  }, [refreshScenesList, instanceId]);

  // Clear all scenes function
  const clearAllScenes = useCallback(async () => {
    if (
      window.confirm('Are you sure you want to delete ALL saved scenes? This cannot be undone.')
    ) {
      setIsLoading(true);
      try {
        const result = await parallaxService.clearAllScenes();
        if (result) {
          setSavedScenes([]);
          setSceneName(`${instanceId}_scene`);
          setCurrentSceneName(null);

          // Clear the current scene assignment for this instance in the database
          await parallaxService.clearInstanceAssignment(instanceId);
        }
      } catch (error) {
        console.error('❌ Error clearing scenes:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [instanceId]);

  // Clear current scene assignment for this instance
  const clearCurrentSceneAssignment = useCallback(async () => {
    if (
      window.confirm(
        'Clear the current scene assignment for this instance? It will load the first available scene next time.'
      )
    ) {
      setCurrentSceneName(null);
      await parallaxService.clearInstanceAssignment(instanceId);
      // Scene assignment cleared
    }
  }, [instanceId]);

  // Save function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveScene = useCallback(async () => {
    // Saving scene data

    if (!sceneName.trim()) {
      alert('Please enter a scene name');
      return;
    }

    setIsLoading(true);
    try {
      // Map layer names back to layer-1, layer-2 format for saving
      const mappedYPositions = {};
      const mappedVisibility = {};
      const mappedSpeeds = {};

      availableSizes.forEach((size, index) => {
        const layerKey = `layer-${index + 1}`;
        mappedYPositions[layerKey] = layerYPositions[size] || 0;
        mappedVisibility[layerKey] = layerVisibility[size] !== false;
        mappedSpeeds[layerKey] = layerSpeeds[size] || 0;
      });

      const sceneData = {
        name: sceneName.trim(),
        category: category,
        component_height: componentHeight,
        layer_y_positions: JSON.stringify(mappedYPositions),
        layer_visibility: JSON.stringify(mappedVisibility),
        layer_speeds: JSON.stringify(mappedSpeeds),
        scene_stickers: JSON.stringify(loadedStickerData), // Save sticker data
        sticker_positions: JSON.stringify(stickerPositions), // Save sticker positions
        background_path: instanceBackgroundPath,
        background_style: JSON.stringify(backgroundStyle),
        description: `Parallax scene: ${sceneName.trim()}`,
        created_at: new Date().toISOString(),
      };

      // Use parallax service to save
      const result = await parallaxService.saveScene(sceneData);

      if (result) {
        setLastSaved(new Date());
        setCurrentSceneName(sceneName.trim());

        // Notify parent component of scene name change
        if (onSceneNameUpdate) {
          onSceneNameUpdate(sceneName.trim());
        }

        // Save this scene as the current scene for this instance in the database
        await parallaxService.saveInstanceAssignment(instanceId, sceneName.trim());

        // Refresh saved scenes list
        const scenes = await parallaxService.getAllScenes();
        const scenesArray = Array.isArray(scenes) ? scenes : scenes?.data || [];
        setSavedScenes(scenesArray);

        // Scene saved successfully
      } else {
        console.error('❌ Failed to save scene');
      }
    } catch (error) {
      console.error('❌ Error saving scene:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    sceneName,
    instanceId,
    category,
    componentHeight,
    layerYPositions,
    layerVisibility,
    layerSpeeds,
    loadedStickerData,
    stickerPositions,
    availableSizes,
    instanceBackgroundPath,
    backgroundStyle,
  ]);

  // Load function for admin panel (separate from useEffect to avoid circular dependency)
  // Image preloading function for svgPath stickers
  const preloadSceneImages = useCallback(async (stickers) => {
    if (!stickers || stickers.length === 0) {
      setAreImagesLoaded(true);
      setImageLoadingProgress(100);
      return;
    }

    setAreImagesLoaded(false);
    setImageLoadingProgress(0);

    // Get unique svgPath URLs from stickers
    const svgPaths = stickers
      .map((sticker) => sticker.svgPath)
      .filter(Boolean)
      .filter((path, index, arr) => arr.indexOf(path) === index); // Remove duplicates

    if (svgPaths.length === 0) {
      setAreImagesLoaded(true);
      setImageLoadingProgress(100);
      return;
    }

    let loadedCount = 0;
    const totalImages = svgPaths.length;

    const imagePromises = svgPaths.map((svgPath) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          loadedCount++;
          const progress = Math.round((loadedCount / totalImages) * 100);
          setImageLoadingProgress(progress);
          resolve();
        };
        img.onerror = () => {
          loadedCount++;
          const progress = Math.round((loadedCount / totalImages) * 100);
          setImageLoadingProgress(progress);
          logger.warn('❌ Failed to load image:', svgPath);
          resolve(); // Still resolve to not block other images
        };
        img.src = svgPath;
      });
    });

    try {
      await Promise.all(imagePromises);
      setAreImagesLoaded(true);
      setImageLoadingProgress(100);
    } catch (error) {
      console.error('❌ Error preloading images:', error);
      setAreImagesLoaded(true);
      setImageLoadingProgress(100);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadScene = useCallback(
    async (selectedSceneName) => {
      if (!selectedSceneName || isLoadingScene) {
        return;
      }

      setIsLoading(true);
      setIsLoadingScene(true);
      try {
        const scene = await parallaxService.loadScene(selectedSceneName);

        if (scene) {
          const sceneData = scene;

          // Load the data
          if (sceneData.layer_y_positions) {
            try {
              // API already parses JSON - use data directly
              const yPositions = sceneData.layer_y_positions;
              const mappedYPositions = {};
              if (yPositions['ATOM'] !== undefined) {
                availableSizes.forEach((size) => {
                  mappedYPositions[size] = yPositions[size] || 0;
                });
              } else {
                availableSizes.forEach((size, index) => {
                  const layerKey = `layer-${index + 1}`;
                  mappedYPositions[size] = yPositions[layerKey] || 0;
                });
              }
              setLayerYPositions(mappedYPositions);
            } catch (e) {
              console.error('Error parsing Y positions:', e);
            }
          }
          if (sceneData.layer_visibility) {
            try {
              // API already parses JSON - use data directly
              const visibility = sceneData.layer_visibility;
              const mappedVisibility = {};
              if (visibility['ATOM'] !== undefined) {
                availableSizes.forEach((size) => {
                  mappedVisibility[size] = visibility[size] !== false;
                });
              } else {
                availableSizes.forEach((size, index) => {
                  const layerKey = `layer-${index + 1}`;
                  mappedVisibility[size] = visibility[layerKey] !== false;
                });
              }
              setLayerVisibility(mappedVisibility);
            } catch (e) {
              console.error('Error parsing visibility:', e);
            }
          }
          if (sceneData.layer_speeds) {
            try {
              // API already parses JSON - use data directly
              const speeds = sceneData.layer_speeds;
              const mappedSpeeds = {};
              if (speeds['ATOM'] !== undefined) {
                availableSizes.forEach((size) => {
                  mappedSpeeds[size] = speeds[size] || 0;
                });
              } else {
                availableSizes.forEach((size, index) => {
                  const layerKey = `layer-${index + 1}`;
                  mappedSpeeds[size] = speeds[layerKey] || 0;
                });
              }
              setLayerSpeeds(mappedSpeeds);
            } catch (e) {
              console.error('Error parsing speeds:', e);
            }
          }
          if (sceneData.component_height) {
            setComponentHeight(sceneData.component_height);
            setTargetHeight(sceneData.component_height);
          }

          // Load sticker positions
          if (sceneData.scene_stickers) {
            try {
              // API already parses JSON - use data directly
              const stickers = sceneData.scene_stickers;
              const newStickerPositions = {};
              stickers.forEach((sticker, index) => {
                const stickerId = `${sticker.listingId}-${sticker.size}-${index}`;
                newStickerPositions[stickerId] = {
                  x: sticker.xOffset || 0,
                  y: sticker.yOffset || 0,
                };
              });
              setStickerPositions(newStickerPositions);
            } catch (e) {
              console.error('Error parsing sticker positions:', e);
            }
          }

          // Load background path if available
          if (sceneData.background_path) {
            setInstanceBackgroundPath(sceneData.background_path);
          }

          // Load background style if available
          if (sceneData.background_style) {
            try {
              // API already parses JSON - use data directly
              const style = sceneData.background_style;
              setBackgroundStyle(style);
            } catch (e) {
              console.error('Error parsing background style:', e);
              // Set to defaults if parsing fails
              setBackgroundStyle({
                size: 'cover',
                position: 'center',
                repeat: 'no-repeat',
                attachment: 'scroll',
              });
            }
          }

          // Check if scene has stickers
          if (sceneData.scene_stickers) {
            try {
              // API already parses JSON - use data directly
              const stickers = sceneData.scene_stickers;

              // Validate and clean the loaded sticker data
              const cleanedStickerData = stickers.map((sticker, index) => {
                const validSize = [
                  'ATOM',
                  'PIXEL',
                  'NANO',
                  'MICRO',
                  'SMALL',
                  'MEDIUM',
                  'LARGE',
                  'JUMBO',
                ].includes(sticker.size)
                  ? sticker.size
                  : 'MICRO';

                const cleanedSticker = {
                  listingId: sticker.listingId?.toString() || `fallback-${index}`,
                  title: sticker.title || 'Untitled Sticker',
                  size: validSize,
                  color: sticker.color || 'Black (Matte)',
                  category: sticker.category || 'stickers',
                  svgPath: sticker.svgPath || null,
                  xOffset: sticker.xOffset || 0,
                  yOffset: sticker.yOffset || 0,
                };

                return cleanedSticker;
              });

              // Update internal state so the component actually displays the loaded stickers
              setLoadedStickerData(cleanedStickerData);
              if (onSceneStickersUpdate) {
                onSceneStickersUpdate(cleanedStickerData);
              }
            } catch (e) {
              console.error('Error parsing scene stickers:', e);
            }
          }

          // Mark this scene as the current one for this instance
          setCurrentSceneName(selectedSceneName);
          setSceneName(selectedSceneName); // Update the scene name input so saves go to the correct scene

          // Notify parent component of scene name change
          if (onSceneNameUpdate) {
            onSceneNameUpdate(selectedSceneName);
          }

          // Scene loaded successfully
        } else {
          console.error('❌ Failed to load scene - no scene data');
        }
      } catch (error) {
        console.error('❌ Error loading scene:', error);
      } finally {
        setIsLoading(false);
        setIsLoadingScene(false);
        // Loading finished
      }
    },
    [availableSizes, instanceId]
  );

  // Speed matrix (LARGE to ATOM)
  const SPEED_MATRIX = [21.0, 7.8, 3.0, 2.0, 1.5, 1.0, 0.5];

  // Initialize speeds for all layers (ignore visibility)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const newSpeeds = {};

    // Assign speeds from matrix to all layers (largest gets fastest)
    // availableSizes is ['ATOM', 'PIXEL', 'NANO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE']
    // SPEED_MATRIX is [21.0, 7.8, 3.0, 2.0, 1.5, 1.0, 0.5] for [LARGE, MEDIUM, SMALL, MICRO, NANO, PIXEL, ATOM]
    // So we need to reverse the availableSizes to match the matrix order
    const reversedSizes = availableSizes.slice().reverse();

    reversedSizes.forEach((size, index) => {
      newSpeeds[size] = SPEED_MATRIX[index] || SPEED_MATRIX[SPEED_MATRIX.length - 1];
    });

    setLayerSpeeds(newSpeeds);
  }, [availableSizes]);

  // Layer control handlers
  const handleYPositionChange = useCallback((size, value) => {
    setLayerYPositions((prev) => {
      const newPositions = {
        ...prev,
        [size]: parseFloat(value) || 0,
      };
      return newPositions;
    });
  }, []);

  const handleVisibilityChange = useCallback((size, visible) => {
    // Disabled - all layers always visible
    // setLayerVisibility(prev => {
    //   const newVisibility = {
    //     ...prev,
    //     [size]: visible
    //   };
    //   return newVisibility;
    // });
  }, []);

  const handleSpeedChange = useCallback((size, value) => {
    setLayerSpeeds((prev) => {
      const newSpeeds = {
        ...prev,
        [size]: parseFloat(value) || 0,
      };
      return newSpeeds;
    });
  }, []);

  // Category assignment function
  const assignSceneToCategory = useCallback(
    async (category) => {
      if (!currentSceneName) {
        alert('Please load a scene first before assigning it to a category.');
        return;
      }

      try {
        setAssigningToCategory(true);
        const result = await parallaxService.saveCategorySceneAssignment(
          category,
          currentSceneName
        );

        if (result) {
          // Scene assigned to category

          // Update local assignments
          setCategoryAssignments((prev) => ({
            ...prev,
            [category]: currentSceneName,
          }));

          alert(`Scene "${currentSceneName}" assigned to category "${category}" successfully!`);
        } else {
          alert('Failed to assign scene to category. Please try again.');
        }
      } catch (error) {
        console.error('❌ Error assigning scene to category:', error);
        alert('Error assigning scene to category. Please try again.');
      } finally {
        setAssigningToCategory(false);
      }
    },
    [currentSceneName]
  );

  // Listing-based scene insertion functions
  const loadListingSceneInsertions = useCallback(async () => {
    try {
      const insertions = await parallaxService.getAllListingSceneInsertions();
      setListingSceneInsertions(insertions);
    } catch (error) {
      console.error('❌ Error loading listing scene insertions:', error);
    }
  }, []);

  // Load listing scene insertions when admin panel is opened
  useEffect(() => {
    if (showAdmin) {
      loadListingSceneInsertions();
    }
  }, [showAdmin, loadListingSceneInsertions]);

  const selectListingForInsertion = useCallback((listing) => {
    setSelectedListingForInsertion(listing);
    setSelectedSceneForInsertion('');
  }, []);

  const assignSceneToListing = useCallback(async () => {
    if (!selectedListingForInsertion || !selectedSceneForInsertion) {
      alert('Please select both a listing and a scene.');
      return;
    }

    try {
      setAssigningSceneToListing(true);
      const listingId =
        selectedListingForInsertion.etsyListingId || selectedListingForInsertion.etsy_listing_id;

      const result = await parallaxService.saveListingSceneInsertion(
        listingId,
        selectedSceneForInsertion
      );

      if (result) {
        alert(
          `Scene "${selectedSceneForInsertion}" assigned to listing "${selectedListingForInsertion.title}" successfully!`
        );

        // Refresh the insertions list
        await loadListingSceneInsertions();

        // Clear selections
        setSelectedListingForInsertion(null);
        setSelectedSceneForInsertion('');
      } else {
        alert('Failed to assign scene to listing. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error assigning scene to listing:', error);
      alert('Error assigning scene to listing. Please try again.');
    } finally {
      setAssigningSceneToListing(false);
    }
  }, [selectedListingForInsertion, selectedSceneForInsertion, loadListingSceneInsertions]);

  const deleteListingSceneInsertion = useCallback(
    async (listingId) => {
      if (window.confirm('Are you sure you want to remove this scene insertion?')) {
        try {
          const result = await parallaxService.deleteListingSceneInsertion(listingId);
          if (result) {
            await loadListingSceneInsertions();
          } else {
            alert('Failed to delete scene insertion. Please try again.');
          }
        } catch (error) {
          console.error('❌ Error deleting listing scene insertion:', error);
          alert('Error deleting scene insertion. Please try again.');
        }
      }
    },
    [loadListingSceneInsertions]
  );

  // Calculate parallax offset for a layer
  const getParallaxOffset = useCallback(
    (size) => {
      if (parallaxMode === 'static' || isEditMode) return 0;

      // Only start parallax when component is in viewport
      if (!isInViewport) return 0;

      const speed = layerSpeeds[size] || 0;
      const yPosition = layerYPositions[size] || 50;

      // Calculate scroll offset relative to when component entered viewport
      const relativeScrollOffset = scrollOffset - viewportEntryScroll;

      // Simple parallax calculation: use relative scroll offset
      return relativeScrollOffset * speed * 0.1;
    },
    [
      scrollOffset,
      layerSpeeds,
      layerYPositions,
      parallaxMode,
      isEditMode,
      isInViewport,
      viewportEntryScroll,
    ]
  );

  // Get background image
  const backgroundImageUrl = useMemo(() => {
    // Use instance-specific background path if available
    if (instanceBackgroundPath) {
      return instanceBackgroundPath;
    }

    // Fall back to category-based backgrounds
    const backgroundMap = {
      dinosaurs: ASSET_PATHS.BKG_DINO,
      cryptids: ASSET_PATHS.BKG_CRYPTID,
      cartoon: ASSET_PATHS.BKG_CARTOON,
      horror: ASSET_PATHS.BKG_HORROR,
      military: ASSET_PATHS.BKG_MILITARY,
    };
    const fallbackBackground = backgroundMap[category] || ASSET_PATHS.BKG_GENERIC;
    return fallbackBackground;
  }, [category, instanceBackgroundPath, instanceId]);

  // Background image loading
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setIsBackgroundLoaded(true);
      // Background image loaded
      // Note: Don't set initialized here - let the main useEffect handle it
      // after ALL loading states (background, scene, images) are complete
    };
    img.onerror = () => {
      // If background fails to load, still mark as loaded
      setIsBackgroundLoaded(true);
      logger.warn('❌ Background image failed to load:', backgroundImageUrl);
      // Note: Don't set initialized here - let the main useEffect handle it
    };
    img.src = backgroundImageUrl;
  }, [backgroundImageUrl]);

  // Update target height when component height changes (for admin panel changes)
  useEffect(() => {
    setTargetHeight(componentHeight);
  }, [componentHeight]);

  // Ensure scene-loaded heights always take precedence over prop heights
  useEffect(() => {
    // When a scene is loaded and has a specific height, always apply it
    if (currentSceneName && componentHeight && componentHeight !== height) {
      setTargetHeight(componentHeight);
    }
  }, [currentSceneName, componentHeight, height, instanceId]);

  // Handle scene data loading after background and images are ready
  useEffect(() => {
    if (isBackgroundLoaded && isSceneLoaded && areImagesLoaded && !isInitialized) {
      // All loading complete - initializing scene
      setLoadingProgress(100); // All loaded
      setTimeout(() => {
        setIsInitialized(true);
      }, 100);
    }
  }, [isBackgroundLoaded, isSceneLoaded, areImagesLoaded, isInitialized, instanceId]);

  // Track if we've already warned about missing stickers
  const hasWarnedRef = useRef(false);

  // Get stickers for a specific size layer
  const getVisibleStickersForSize = useCallback(
    (size) => {
      // Filter stickers by size - use loaded data if available, otherwise use prop
      const stickersToUse = loadedStickerData.length > 0 ? loadedStickerData : sceneStickers;
      const filtered = stickersToUse.filter((sticker) => sticker.size === size);

      // Only log ONCE per instance when data is missing
      if (stickersToUse.length === 0 && !hasWarnedRef.current) {
        hasWarnedRef.current = true;
        console.warn(
          `⚠️ [${instanceId}] No stickers available - loadedStickerData: ${loadedStickerData.length}, sceneStickers: ${sceneStickers.length}`
        );
      }

      return filtered;
    },
    [loadedStickerData, sceneStickers, instanceId]
  );

  // Mouse drag state
  const [draggedSticker, setDraggedSticker] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Refs for mouse event handlers to avoid stale closures
  const draggedStickerRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const moveHandlerRef = useRef(null);
  const upHandlerRef = useRef(null);

  // Handle sticker drag
  const handleStickerDrag = useCallback((stickerId, newX, newY) => {
    setStickerPositions((prev) => ({
      ...prev,
      [stickerId]: { x: newX, y: newY },
    }));
  }, []);

  // Handle mouse down on sticker
  const handleMouseDown = useCallback(
    (e, stickerId) => {
      if (!isEditMode) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      setDraggedSticker(stickerId);
      setDragOffset({ x: offsetX, y: offsetY });

      // Update refs
      draggedStickerRef.current = stickerId;
      dragOffsetRef.current = { x: offsetX, y: offsetY };

      // Create move handler
      moveHandlerRef.current = (e) => {
        if (!draggedStickerRef.current || !isEditMode) {
          return;
        }

        const containerRect = parallaxRef.current?.getBoundingClientRect();
        if (!containerRect) {
          return;
        }

        const newX = e.clientX - containerRect.left - dragOffsetRef.current.x;
        const newY = e.clientY - containerRect.top - dragOffsetRef.current.y;

        handleStickerDrag(draggedStickerRef.current, newX, newY);
      };

      // Create up handler
      upHandlerRef.current = () => {
        setDraggedSticker(null);
        setDragOffset({ x: 0, y: 0 });

        // Clear refs
        draggedStickerRef.current = null;
        dragOffsetRef.current = { x: 0, y: 0 };

        // Remove global listeners
        document.removeEventListener('mousemove', moveHandlerRef.current);
        document.removeEventListener('mouseup', upHandlerRef.current);
      };

      // Add global mouse event listeners
      document.addEventListener('mousemove', moveHandlerRef.current);
      document.addEventListener('mouseup', upHandlerRef.current);
    },
    [isEditMode, handleStickerDrag]
  );

  // Toggle edit mode
  const toggleEditMode = useCallback(() => {
    setIsEditMode(!isEditMode);
  }, [isEditMode]);

  // Dynamic background styles
  const getBackgroundStyles = useMemo(() => {
    const baseStyles = {
      backgroundImage: isBackgroundLoaded ? `url(${backgroundImageUrl})` : 'none',
      backgroundColor: isBackgroundLoaded ? 'transparent' : '#1a1a1a',
    };

    // Apply user-defined styles
    if (backgroundStyle.size === 'tile') {
      return {
        ...baseStyles,
        backgroundSize: 'auto',
        backgroundPosition: 'top left',
        backgroundRepeat: backgroundStyle.repeat || 'repeat',
      };
    } else if (backgroundStyle.size === 'fill') {
      return {
        ...baseStyles,
        backgroundSize: '100% 100%',
        backgroundPosition: backgroundStyle.position || 'center',
        backgroundRepeat: 'no-repeat',
      };
    } else {
      // cover, contain, or any other size
      return {
        ...baseStyles,
        backgroundSize: backgroundStyle.size || 'cover',
        backgroundPosition: backgroundStyle.position || 'center',
        backgroundRepeat: backgroundStyle.repeat || 'no-repeat',
      };
    }
  }, [backgroundImageUrl, isBackgroundLoaded, backgroundStyle]);

  // Force correct initialization logic - calculate based on all loading states
  const actualIsInitialized = isBackgroundLoaded && isSceneLoaded && areImagesLoaded;

  // ⏱️ TIMING CONTROL: Wait for hero scene to finish expanding before revealing parallax
  // Listen for custom event from HeroScene when animation completes
  // Skip the wait if this is not the initial app load (e.g., when toggling grouping)
  const [heroExpansionComplete, setHeroExpansionComplete] = useState(false);
  useEffect(() => {
    // If not initial load, immediately set as complete (no need to wait for hero animation)
    if (!isInitialAppLoad) {
      logger.log(`⚡ [${instanceId}] Not initial load - skipping hero animation wait`);
      setHeroExpansionComplete(true);
      return;
    }

    const handleHeroComplete = () => {
      logger.log(`🎬 [${instanceId}] Hero expansion complete - parallax can now reveal`);
      setHeroExpansionComplete(true);
    };

    // Listen for hero animation completion event
    window.addEventListener('heroAnimationComplete', handleHeroComplete);

    // Fallback: If hero doesn't dispatch event, reveal after 5 seconds
    const fallbackTimer = setTimeout(() => {
      if (!heroExpansionComplete) {
        logger.log(`⏰ [${instanceId}] Fallback timer - revealing parallax after 5s`);
        setHeroExpansionComplete(true);
      }
    }, 5000);

    return () => {
      window.removeEventListener('heroAnimationComplete', handleHeroComplete);
      clearTimeout(fallbackTimer);
    };
  }, [instanceId, heroExpansionComplete, isInitialAppLoad]);

  // EMERGENCY: Force initialization after 15 seconds to prevent permanent loading
  // Increased from 5s to give images time to actually load
  const [emergencyInitialized, setEmergencyInitialized] = useState(false);
  useEffect(() => {
    const emergencyTimer = setTimeout(() => {
      logger.log(`🚨 [${instanceId}] Emergency initialization triggered after 15 seconds`);
      setEmergencyInitialized(true);
    }, 15000); // Increased from 5000ms

    // Clear timer if proper initialization happens
    if (actualIsInitialized && heroExpansionComplete) {
      clearTimeout(emergencyTimer);
    }

    return () => clearTimeout(emergencyTimer);
  }, [actualIsInitialized, heroExpansionComplete, instanceId]);

  const finalIsInitialized = (actualIsInitialized && heroExpansionComplete) || emergencyInitialized;

  return (
    <div
      ref={parallaxRef}
      className={`parallax-component ${finalIsInitialized ? 'parallax-loaded' : 'parallax-loading'}`}
      style={{
        height: finalIsInitialized ? targetHeight : '4px',
        position: 'relative',
        overflow: isEditMode ? 'auto' : 'hidden',
        width: isEditMode ? '200%' : '100%',
        transition: 'height 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        ...getBackgroundStyles,
      }}
      // Removed click-outside-to-close to allow dragging while admin panel is open
    >
      {/* Loading Indicator */}
      {!finalIsInitialized && (
        <div className="parallax-loading-indicator">
          <div className="loading-progress-bar">
            <div
              className="loading-progress-fill"
              style={{ width: `${Math.max(loadingProgress, imageLoadingProgress)}%` }}
            ></div>
          </div>
          {emergencyInitialized ? (
            <p>Emergency initialization activated</p>
          ) : !isSceneLoaded ? (
            <p>Loading scene data... {loadingProgress}%</p>
          ) : !areImagesLoaded ? (
            <p>Loading images... {imageLoadingProgress}%</p>
          ) : (
            <p>Initializing... 100%</p>
          )}
        </div>
      )}

      {/* Parallax Layers */}
      {finalIsInitialized &&
        availableSizes.map((size) => {
          const stickers = getVisibleStickersForSize(size);

          // Skip creating layers for sizes with no stickers
          if (stickers.length === 0) {
            return null;
          }

          const parallaxOffset = getParallaxOffset(size);
          const yPosition = layerYPositions[size] || 50;

          return (
            <div
              key={size}
              className="parallax-layer"
              style={{
                position: 'absolute',
                top: `${yPosition}%`,
                left: 0,
                right: 0,
                transform: `translateX(${parallaxOffset}px)`,
                zIndex: availableSizes.indexOf(size),
                pointerEvents: isEditMode ? 'auto' : 'none',
              }}
            >
              {stickers.map((sticker, index) => {
                const stickerId = `${sticker.listingId}-${sticker.size}-${index}`;
                const position = stickerPositions[stickerId] || {
                  x: sticker.xOffset || 0,
                  y: sticker.yOffset || 0,
                };
                const isDragging = draggedSticker === stickerId;

                // Skip rendering if missing required fields
                const hasRequiredFields = !!(sticker.listingId && sticker.size && sticker.color);
                if (!hasRequiredFields) {
                  return null;
                }

                return (
                  <div
                    key={stickerId}
                    style={{
                      position: 'absolute',
                      left: `${position.x}px`,
                      top: `${position.y}px`,
                      cursor: isEditMode ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
                      border: isEditMode
                        ? `2px dashed ${isDragging ? '#FF5722' : '#4CAF50'}`
                        : 'none',
                      zIndex: isEditMode ? (isDragging ? 1001 : 1000) : 'auto',
                      transform: isDragging ? 'scale(1.05)' : 'scale(1)',
                      transition: isDragging ? 'none' : 'transform 0.1s ease',
                      userSelect: 'none',
                    }}
                    onMouseDown={(e) => handleMouseDown(e, stickerId)}
                    onClick={(e) => {
                      if (!isEditMode && onStickerClick) {
                        onStickerClick(sticker);
                      }
                    }}
                  >
                    <ActualSizeSticker
                      listingId={sticker.listingId}
                      size={sticker.size}
                      color={sticker.color}
                      svgPath={sticker.svgPath}
                      showDimensions={false}
                      showGrid={false}
                      key={`${sticker.listingId}-${sticker.size}-parallax`}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}

      {/* Admin Toggle Button */}
      {showAdmin && finalIsInitialized && (
        <button
          className="admin-toggle-btn"
          onClick={() => setAdminOpen(!adminOpen)}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1000,
            background: '#333',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          <MaterialIcon icon="settings" />
        </button>
      )}

      {/* Simple Admin Panel */}
      {showAdmin && adminOpen && finalIsInitialized && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            width: 'min(600px, calc(100vw - 40px))',
            maxHeight: 'calc(100vh - 40px)',
            background: '#1a1a1a',
            border: '2px solid #333',
            borderRadius: '8px',
            padding: '15px',
            zIndex: 1000,
            color: 'white',
            fontSize: '14px',
            cursor: 'move',
            overflow: 'auto',
          }}
          onMouseDown={(e) => {
            if (e.target.closest('button, input, select, label')) return;
            const panel = e.currentTarget;
            const startX = e.clientX - panel.offsetLeft;
            const startY = e.clientY - panel.offsetTop;

            const moveHandler = (e) => {
              panel.style.left = `${e.clientX - startX}px`;
              panel.style.top = `${e.clientY - startY}px`;
              panel.style.right = 'auto';
            };

            const upHandler = () => {
              document.removeEventListener('mousemove', moveHandler);
              document.removeEventListener('mouseup', upHandler);
            };

            document.addEventListener('mousemove', moveHandler);
            document.addEventListener('mouseup', upHandler);
          }}
          // Removed stopPropagation to allow normal event flow
        >
          {/* Sticky Close Button */}
          <button
            onClick={() => setAdminOpen(false)}
            style={{
              position: 'fixed',
              top: '25px',
              right: '25px',
              background: '#f44336',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1002,
              fontWeight: 'bold',
            }}
            title="Close Admin Panel"
          >
            ×
          </button>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '15px',
              paddingTop: '10px',
            }}
          >
            <strong>Parallax Admin</strong>
          </div>

          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            {/* Left Column - Settings */}
            <div style={{ flex: '1 1 300px', minWidth: '280px' }}>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Scene Name:</label>
                <input
                  type="text"
                  value={sceneName}
                  onChange={(e) => setSceneName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px',
                    background: '#222',
                    color: 'white',
                    border: '1px solid #555',
                  }}
                  placeholder="Enter scene name"
                />
              </div>

              <button
                onClick={() => parallaxService.saveInstanceAssignment(instanceId, sceneName)}
                disabled={isLoading || !sceneName.trim()}
                style={{
                  width: '100%',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '8px',
                  borderRadius: '4px',
                  cursor: isLoading || !sceneName.trim() ? 'not-allowed' : 'pointer',
                  marginBottom: '10px',
                  fontSize: '12px',
                }}
                title="Save this scene as the default for this instance"
              >
                {isLoading ? 'Saving...' : 'Set as Default Scene'}
              </button>

              {/* Instance Information */}
              <div
                style={{
                  marginBottom: '10px',
                  padding: '8px',
                  background: '#333',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '5px' }}>
                  <strong>Instance ID:</strong> {instanceId}
                </div>
                <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '5px' }}>
                  <strong>Current Scene:</strong> {currentSceneName || 'None'}
                </div>
                <button
                  onClick={clearCurrentSceneAssignment}
                  style={{
                    width: '100%',
                    background: '#ff9800',
                    color: 'white',
                    border: 'none',
                    padding: '4px',
                    borderRadius: '2px',
                    cursor: 'pointer',
                    fontSize: '10px',
                    marginTop: '5px',
                  }}
                >
                  Clear Scene Assignment
                </button>
              </div>

              <button
                onClick={saveScene}
                disabled={isLoading}
                style={{
                  width: '100%',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '8px',
                  borderRadius: '4px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  marginBottom: '10px',
                }}
              >
                {isLoading ? 'Saving...' : 'Save Scene'}
              </button>

              <button
                onClick={clearAllScenes}
                disabled={isLoading}
                style={{
                  width: '100%',
                  background: '#f44336',
                  color: 'white',
                  border: 'none',
                  padding: '8px',
                  borderRadius: '4px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  marginBottom: '10px',
                }}
              >
                {isLoading ? 'Clearing...' : 'Clear All Scenes'}
              </button>

              <button
                onClick={toggleEditMode}
                style={{
                  width: '100%',
                  background: isEditMode ? '#ff9800' : '#2196F3',
                  color: 'white',
                  border: 'none',
                  padding: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginBottom: '10px',
                }}
              >
                {isEditMode ? 'Exit Edit Mode' : 'Edit Position Mode'}
              </button>

              {lastSaved && (
                <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '10px' }}>
                  Saved: {lastSaved.toLocaleTimeString()}
                </div>
              )}

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Load Scene:</label>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <select
                    value={currentSceneName || ''}
                    onChange={(e) => {
                      // Dropdown changed
                      loadScene(e.target.value);
                    }}
                    onFocus={() => {
                      // Refresh scenes list when dropdown is opened
                      // Refreshing scenes list
                      parallaxService
                        .getAllScenes()
                        .then((scenes) => {
                          const scenesArray = Array.isArray(scenes) ? scenes : scenes?.data || [];
                          // Available scenes refreshed
                          setSavedScenes(scenesArray);
                        })
                        .catch((err) => {
                          console.error('❌ Error loading scenes:', err);
                        });
                    }}
                    style={{
                      flex: 1,
                      padding: '5px',
                      background: '#222',
                      color: 'white',
                      border: '1px solid #555',
                    }}
                  >
                    <option value="">
                      Select a scene to load... ({savedScenes.length} available)
                    </option>
                    {Array.isArray(savedScenes) &&
                      savedScenes.map((scene) => (
                        <option key={scene.name} value={scene.name}>
                          {scene.name} ({new Date(scene.created_at).toLocaleDateString()})
                          {scene.name === currentSceneName ? ' (Current)' : ''}
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={async () => {
                      if (!currentSceneName) {
                        alert('Please select a scene first');
                        return;
                      }
                      if (
                        window.confirm(
                          `Are you sure you want to delete scene "${currentSceneName}"? This cannot be undone.`
                        )
                      ) {
                        const success = await parallaxService.deleteScene(currentSceneName);
                        if (success) {
                          // Refresh scenes list
                          const scenes = await parallaxService.getAllScenes();
                          const scenesArray = Array.isArray(scenes) ? scenes : scenes?.data || [];
                          setSavedScenes(scenesArray);
                          setCurrentSceneName(null);
                          alert('Scene deleted successfully');
                        } else {
                          alert('Failed to delete scene');
                        }
                      }
                    }}
                    disabled={!currentSceneName}
                    style={{
                      padding: '5px 10px',
                      background: !currentSceneName ? '#666' : '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: !currentSceneName ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                    }}
                    title="Delete current scene"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Height:</label>
                <input
                  type="text"
                  value={componentHeight}
                  onChange={(e) => setComponentHeight(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px',
                    background: '#222',
                    color: 'white',
                    border: '1px solid #555',
                  }}
                />
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Mode:</label>
                <select
                  value={parallaxMode}
                  onChange={(e) => setParallaxMode(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px',
                    background: '#222',
                    color: 'white',
                    border: '1px solid #555',
                  }}
                >
                  <option value="normal">Normal Parallax</option>
                  <option value="static">Static</option>
                </select>
              </div>

              {/* Background Settings Accordion */}
              <div style={{ marginBottom: '10px', border: '1px solid #555', borderRadius: '4px' }}>
                {/* Accordion Header */}
                <div
                  style={{
                    padding: '8px',
                    background: '#333',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: backgroundAccordionOpen ? '1px solid #555' : 'none',
                  }}
                  onClick={() => setBackgroundAccordionOpen(!backgroundAccordionOpen)}
                >
                  <span style={{ fontWeight: 'bold', fontSize: '12px' }}>Background Settings</span>
                  <span style={{ fontSize: '14px' }}>{backgroundAccordionOpen ? '−' : '+'}</span>
                </div>

                {/* Accordion Content */}
                {backgroundAccordionOpen && (
                  <div style={{ padding: '10px', background: '#222' }}>
                    {/* Background Path */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px' }}>
                        Background Image:
                      </label>
                      <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                        <input
                          type="text"
                          value={instanceBackgroundPath || ''}
                          onChange={(e) => setInstanceBackgroundPath(e.target.value)}
                          placeholder="Enter background image path..."
                          style={{
                            flex: 1,
                            padding: '5px',
                            background: '#111',
                            color: 'white',
                            border: '1px solid #555',
                            fontSize: '11px',
                          }}
                        />
                        <label
                          style={{
                            background: '#555',
                            color: 'white',
                            border: '1px solid #555',
                            padding: '5px 10px',
                            borderRadius: '2px',
                            cursor: 'pointer',
                            fontSize: '10px',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                          }}
                        >
                          Browse Files
                          <input
                            type="file"
                            hidden
                            accept=".jpg,.jpeg,.png,.gif,.webp"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const fileName = file.name;
                                const relativePath = `/assets/images/${fileName}`;
                                setInstanceBackgroundPath(relativePath);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <button
                        onClick={() => {
                          if (currentSceneName) {
                            parallaxService.saveInstanceAssignment(
                              instanceId,
                              currentSceneName,
                              instanceBackgroundPath
                            );
                          }
                        }}
                        disabled={!currentSceneName}
                        style={{
                          width: '100%',
                          background: '#2196F3',
                          color: 'white',
                          border: 'none',
                          padding: '4px',
                          borderRadius: '2px',
                          cursor: currentSceneName ? 'pointer' : 'not-allowed',
                          fontSize: '10px',
                          marginTop: '5px',
                        }}
                      >
                        Save Background Path
                      </button>
                    </div>

                    {/* Background Style Controls */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px' }}>
                        Background Style:
                      </label>

                      {/* Size Control */}
                      <div style={{ marginBottom: '5px' }}>
                        <label style={{ fontSize: '10px' }}>Size:</label>
                        <select
                          value={backgroundStyle.size || 'cover'}
                          onChange={(e) =>
                            setBackgroundStyle((prev) => ({ ...prev, size: e.target.value }))
                          }
                          style={{
                            width: '100%',
                            padding: '3px',
                            background: '#111',
                            color: 'white',
                            border: '1px solid #555',
                            fontSize: '10px',
                          }}
                        >
                          <option value="cover">Cover (Fill container)</option>
                          <option value="contain">Contain (Fit entire image)</option>
                          <option value="fill">Fill (Stretch to fit)</option>
                          <option value="tile">Tile (Repeat pattern)</option>
                        </select>
                      </div>

                      {/* Position Control (only show if not tile) */}
                      {backgroundStyle.size !== 'tile' && (
                        <div style={{ marginBottom: '5px' }}>
                          <label style={{ fontSize: '10px' }}>Position:</label>
                          <select
                            value={backgroundStyle.position || 'center'}
                            onChange={(e) =>
                              setBackgroundStyle((prev) => ({ ...prev, position: e.target.value }))
                            }
                            style={{
                              width: '100%',
                              padding: '3px',
                              background: '#111',
                              color: 'white',
                              border: '1px solid #555',
                              fontSize: '10px',
                            }}
                          >
                            <option value="center">Center</option>
                            <option value="top">Top</option>
                            <option value="bottom">Bottom</option>
                            <option value="left">Left</option>
                            <option value="right">Right</option>
                            <option value="top-left">Top Left</option>
                            <option value="top-right">Top Right</option>
                            <option value="bottom-left">Bottom Left</option>
                            <option value="bottom-right">Bottom Right</option>
                          </select>
                        </div>
                      )}

                      {/* Repeat Control (only show if tile) */}
                      {backgroundStyle.size === 'tile' && (
                        <div style={{ marginBottom: '5px' }}>
                          <label style={{ fontSize: '10px' }}>Repeat:</label>
                          <select
                            value={backgroundStyle.repeat || 'repeat'}
                            onChange={(e) =>
                              setBackgroundStyle((prev) => ({ ...prev, repeat: e.target.value }))
                            }
                            style={{
                              width: '100%',
                              padding: '3px',
                              background: '#111',
                              color: 'white',
                              border: '1px solid #555',
                              fontSize: '10px',
                            }}
                          >
                            <option value="repeat">Repeat Both</option>
                            <option value="repeat-x">Repeat Horizontal</option>
                            <option value="repeat-y">Repeat Vertical</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Category Scene Assignment */}
              <div
                style={{
                  marginBottom: '10px',
                  padding: '10px',
                  background: '#333',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '12px' }}>
                  Category Assignment
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px' }}>
                    Assign to Category:
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '3px',
                      background: '#222',
                      color: 'white',
                      border: '1px solid #555',
                      fontSize: '11px',
                    }}
                  >
                    <option value="">Select category...</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}{' '}
                        {categoryAssignments[category] ? `(${categoryAssignments[category]})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => assignSceneToCategory(selectedCategory)}
                  disabled={!selectedCategory || !currentSceneName || assigningToCategory}
                  style={{
                    width: '100%',
                    background: '#9c27b0',
                    color: 'white',
                    border: 'none',
                    padding: '4px',
                    borderRadius: '2px',
                    cursor:
                      !selectedCategory || !currentSceneName || assigningToCategory
                        ? 'not-allowed'
                        : 'pointer',
                    fontSize: '10px',
                    marginBottom: '5px',
                  }}
                >
                  {assigningToCategory ? 'Assigning...' : 'Assign Scene to Category'}
                </button>

                {currentSceneName && (
                  <div style={{ fontSize: '10px', color: '#ccc', marginTop: '5px' }}>
                    Current: {currentSceneName}
                  </div>
                )}
              </div>

              {/* Listing-Based Scene Insertion */}
              <div
                style={{
                  marginBottom: '10px',
                  padding: '10px',
                  background: '#333',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '12px' }}>
                  Listing-Based Scene Insertion
                </div>

                {/* Search Listings */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px' }}>
                    Search Listings:
                  </label>
                  <input
                    type="text"
                    value={listingSearchTerm}
                    onChange={(e) => setListingSearchTerm(e.target.value)}
                    placeholder="Search by title, category, etc..."
                    style={{
                      width: '100%',
                      padding: '3px',
                      background: '#222',
                      color: 'white',
                      border: '1px solid #555',
                      fontSize: '11px',
                    }}
                  />
                </div>

                {/* Search Results */}
                {listingSearchResults.length > 0 && (
                  <div
                    style={{
                      marginBottom: '8px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      background: '#222',
                      border: '1px solid #555',
                      borderRadius: '2px',
                    }}
                  >
                    {listingSearchResults.slice(0, 10).map((listing, index) => (
                      <div
                        key={listing.etsyListingId || `listing-${index}`}
                        style={{
                          padding: '5px',
                          borderBottom: '1px solid #444',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: '10px',
                              fontWeight: 'bold',
                              color: '#fff',
                              marginBottom: '2px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {listing.title}
                          </div>
                          <div style={{ fontSize: '9px', color: '#ccc' }}>
                            {listing.category || 'No category'} • ID:{' '}
                            {listing.etsyListingId || listing.etsy_listing_id || 'No ID'}
                          </div>
                        </div>
                        <button
                          onClick={() => selectListingForInsertion(listing)}
                          style={{
                            background: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            padding: '2px 6px',
                            borderRadius: '2px',
                            cursor: 'pointer',
                            fontSize: '9px',
                            marginLeft: '5px',
                          }}
                        >
                          Select
                        </button>
                      </div>
                    ))}
                    {listingSearchResults.length > 10 && (
                      <div
                        style={{
                          padding: '5px',
                          textAlign: 'center',
                          fontSize: '9px',
                          color: '#ccc',
                        }}
                      >
                        Showing first 10 of {listingSearchResults.length} results...
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Listing and Scene Assignment */}
                {selectedListingForInsertion && (
                  <div
                    style={{
                      marginBottom: '8px',
                      padding: '5px',
                      background: '#222',
                      borderRadius: '2px',
                    }}
                  >
                    <div style={{ fontSize: '10px', color: '#ccc', marginBottom: '3px' }}>
                      Selected Listing:
                    </div>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px' }}>
                      {selectedListingForInsertion.title}
                    </div>

                    <div style={{ marginBottom: '5px' }}>
                      <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px' }}>
                        Assign Scene:
                      </label>
                      <select
                        value={selectedSceneForInsertion}
                        onChange={(e) => setSelectedSceneForInsertion(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '3px',
                          background: '#111',
                          color: 'white',
                          border: '1px solid #555',
                          fontSize: '10px',
                        }}
                      >
                        <option value="">Select scene...</option>
                        {Array.isArray(savedScenes) &&
                          savedScenes.map((scene) => (
                            <option key={scene.name} value={scene.name}>
                              {scene.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={assignSceneToListing}
                        disabled={!selectedSceneForInsertion || assigningSceneToListing}
                        style={{
                          flex: 1,
                          background: '#FF5722',
                          color: 'white',
                          border: 'none',
                          padding: '3px',
                          borderRadius: '2px',
                          cursor:
                            !selectedSceneForInsertion || assigningSceneToListing
                              ? 'not-allowed'
                              : 'pointer',
                          fontSize: '9px',
                        }}
                      >
                        {assigningSceneToListing ? 'Assigning...' : 'Assign Scene'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedListingForInsertion(null);
                          setSelectedSceneForInsertion('');
                        }}
                        style={{
                          background: '#666',
                          color: 'white',
                          border: 'none',
                          padding: '3px 6px',
                          borderRadius: '2px',
                          cursor: 'pointer',
                          fontSize: '9px',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Show Current Listing Scene Insertions */}
                {listingSceneInsertions.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '10px', color: '#ccc', marginBottom: '3px' }}>
                      Current Assignments:
                    </div>
                    <div
                      style={{
                        maxHeight: '100px',
                        overflow: 'auto',
                        background: '#222',
                        border: '1px solid #555',
                        borderRadius: '2px',
                      }}
                    >
                      {listingSceneInsertions.map((insertion) => (
                        <div
                          key={insertion.listing_id}
                          style={{
                            padding: '3px 5px',
                            borderBottom: '1px solid #444',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '9px', color: '#fff' }}>
                              ID: {insertion.listing_id}
                            </div>
                            <div style={{ fontSize: '8px', color: '#ccc' }}>
                              → {insertion.scene_name}
                            </div>
                          </div>
                          <button
                            onClick={() => deleteListingSceneInsertion(insertion.listing_id)}
                            style={{
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              padding: '1px 4px',
                              borderRadius: '2px',
                              cursor: 'pointer',
                              fontSize: '8px',
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Layer Controls */}
            <div style={{ flex: '1 1 300px', minWidth: '280px' }}>
              {/* Layer Controls Accordion */}
              <div style={{ marginBottom: '10px' }}>
                <button
                  onClick={() => setLayerControlsAccordionOpen(!layerControlsAccordionOpen)}
                  style={{
                    width: '100%',
                    background: '#333',
                    color: 'white',
                    border: '1px solid #555',
                    padding: '8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                  }}
                >
                  <span>Layer Controls</span>
                  <span
                    style={{
                      fontSize: '16px',
                      transition: 'transform 0.2s',
                      transform: layerControlsAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    ▼
                  </span>
                </button>

                {layerControlsAccordionOpen && (
                  <div
                    style={{
                      background: '#222',
                      border: '1px solid #555',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      padding: '10px',
                      maxHeight: '300px',
                      overflow: 'auto',
                    }}
                  >
                    {availableSizes.map((size) => {
                      const yPosition = layerYPositions[size] || 0;
                      const speed = layerSpeeds[size] || 0;
                      const visible = layerVisibility[size] !== false;

                      return (
                        <div
                          key={size}
                          style={{
                            marginBottom: '8px',
                            padding: '8px',
                            background: '#333',
                            borderRadius: '4px',
                          }}
                        >
                          <div
                            style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '12px' }}
                          >
                            {size}
                          </div>
                          <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                            <input
                              type="number"
                              min="-100"
                              max="100"
                              value={yPosition}
                              onChange={(e) => handleYPositionChange(size, e.target.value)}
                              style={{
                                width: '50%',
                                padding: '3px',
                                background: '#111',
                                color: 'white',
                                border: '1px solid #555',
                                fontSize: '11px',
                              }}
                              placeholder="Y"
                            />
                            <input
                              type="number"
                              value={speed}
                              onChange={(e) => handleSpeedChange(size, e.target.value)}
                              style={{
                                width: '50%',
                                padding: '3px',
                                background: '#111',
                                color: 'white',
                                border: '1px solid #555',
                                fontSize: '11px',
                              }}
                              placeholder="Speed"
                            />
                          </div>
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '11px',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              onChange={(e) => handleVisibilityChange(size, e.target.checked)}
                            />
                            <span>Show (Always)</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ParallaxComponent.propTypes = {
  category: PropTypes.string,
  availableSizes: PropTypes.arrayOf(PropTypes.string),
  height: PropTypes.string,
  instanceId: PropTypes.string,
  sceneStickers: PropTypes.arrayOf(PropTypes.object),
  loadSceneName: PropTypes.string,
  onStickerClick: PropTypes.func,
  onSceneStickersUpdate: PropTypes.func,
  onSceneNameUpdate: PropTypes.func,
  showAdmin: PropTypes.bool,
  isInitialAppLoad: PropTypes.bool,
};

export default ParallaxComponent;
